import { useAuthQuery } from '@/api/index.ts';
import { studyQueryKeys } from '@/config/queryKeys';
import { canUserEditStudy } from '@/config/studies';
import { AssaySamplesPanelGroup } from '@/pages/Study/components/AssaySamplesPanelGroup';
import { StudyInfoSection } from '@/pages/Study/components/StudyInfoSection';
import { StudySamplesTable } from '@/pages/Study/components/StudySamplesTable';
import { StudyTitleBar } from '@/pages/Study/components/StudyTitleBar';
import { fetchStudy } from '@/services/studies';
import { useAppStore } from '@/stores/app';
import { Study } from '@/types';
import { FullLoading } from '@components/index';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { useSuspenseQuery } from '@tanstack/react-query';
import { ErrorComponent, createLazyFileRoute } from '@tanstack/react-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import studyStyles from '../../pages/Studies/styles.module.css';
import '../../styles/components/ag-grid-overrides.css';
import detailsPageStyles from '../../styles/components/details.page.module.css';
import pageStyles from '../../styles/pages/index.module.css';

export const Route = createLazyFileRoute('/studies/$studyId')({
  component: RouteComponent,
  errorComponent: e => <ErrorComponent {...e} />,
  pendingComponent: () => {
    const { t } = useTranslation('studies');

    return <FullLoading label={`${t('fetching')}...`} />;
  },
});

function RouteComponent() {
  const { t } = useTranslation('studies');
  // Mirror the loader's fallback: selectedOrganization is not persisted and is undefined
  // on the first render after a hard reload. selectedOrgStub IS persisted (localStorage,
  // synchronous), so it's available immediately. Without the fallback, selectedOrg is null
  // on render 1 (early return, fewer hooks) then non-null on render 2 — violating the
  // Rules of Hooks.
  const selectedOrg = useAppStore(useShallow(state => state.selectedOrganization ?? state.selectedOrgStub));
  const { studyId } = Route.useParams();
  const orgId = selectedOrg?.id ?? '';
  const queryKey = studyQueryKeys.detail(orgId, studyId);

  const { data, isFetching } = useSuspenseQuery<Study>({
    queryKey,
    queryFn: () => fetchStudy(orgId, studyId),
  });

  const { data: currentUser } = useAuthQuery();
  const canEdit = canUserEditStudy(data, currentUser ?? null);

  if (!selectedOrg?.id) {
    return <ErrorComponent error={new Error(t('study.no_org_selected'))} />;
  }

  const emptyTxt = t('empty', { ns: 'general' });

  return (
    <Stack as="section" className={studyStyles.container} fullWidth orientation="vertical">
      <StudyTitleBar
        canEdit={canEdit}
        data={data}
        orgId={orgId}
        pageStyles={pageStyles}
        studyStyles={studyStyles}
        t={t}
      />
      <Stack fullWidth gap={4} orientation="vertical">
        <Stack as="header" className={detailsPageStyles.header}>
          <StudyInfoSection data={data} isLoading={isFetching} t={t} emptyTxt={emptyTxt} />
        </Stack>

        <StudySamplesTable
          orgId={orgId}
          studyId={studyId}
          totalSamples={data?.num_samples ?? 0}
          passedQc={data?.num_samples_passed_qc ?? 0}
        />

        <AssaySamplesPanelGroup orgId={orgId} studyId={studyId} study={data} />
      </Stack>
    </Stack>
  );
}
